<template>
  <!-- eslint-disable vue/valid-template-root -->
  <button
    class="notif-icon-wrapper"
    :aria-label="`Notifications, ${hasIndicator && totalNotifications >= 1 ? totalNotifications : 0} New`"
    @click.stop="openNotificationModal"
  >
    <div class="notif-icon" :class="brand === 'mcom' ? 'email-filled' : 'email'">
      <template v-if="hasIndicator">
        <span class="notif-indicator">
          <span v-if="totalNotifications > 1">{{ totalNotifications }} </span>
        </span>
      </template>
    </div>
  </button>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { Cookie } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import { onlineUid, brand } from 'config';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import useSlideout from '@/composables/useSlideout';

const { showSlideout } = useSlideout();
const notificationStore = useNotificationStore();
const envPropsStore = useEnvPropsStore();

const { notifications, hasIndicator } = storeToRefs(notificationStore);
const totalNotifications = computed(() => notificationStore.getTotalNotifications);

const showIndicator = (payload) => notificationStore.setIndicator(payload);

const updateIndicator = () => {
  // get previous data from storage and compare to current data
  // TODO:: Need to check the work around for secure_user_token ID, since it is now read only token.
  const sessionId = Cookie.get(onlineUid);
  const prevNotif = sessionStorage.getItem('which-notifications-read');
  const parsedPrev = prevNotif ? JSON.parse(prevNotif) : null;
  const currNotif = notifications.value;

  // return false if no notifications
  if (totalNotifications.value < 1) {
    return false;
  }

  // check if notifications have been read for session
  const notifRead = sessionId && sessionStorage.getItem('notifications-read') === sessionId;

  // if no previous notifications were stored, show indicator if not read
  if (!parsedPrev) {
    return !notifRead;
  }

  // check for new notifications by type
  const simpleNotificationKeys = ['addCardToWallet', 'makePropCardDefault', 'enrollLoyalty', 'starRewardsDowngradeInfo'];

  const listNotificationKeys = ['linkPropCardsToFusion', 'loyaltyRewardCards', 'orderInfo'];

  const hasNewSimpleNotifications = simpleNotificationKeys.some((key) => !parsedPrev[key] && currNotif[key]);

  const hasNewListNotifications = listNotificationKeys.some(
    (key) =>
      (!parsedPrev[key] && currNotif[key]) || // new list
      (parsedPrev[key] && currNotif[key] && parsedPrev[key].length < currNotif[key].length),
  );

  const newNotifications = hasNewSimpleNotifications || hasNewListNotifications;

  return !notifRead || newNotifications;
};

const openNotificationModal = () => {
  const userId = Cookie.get(onlineUid);
  showSlideout('inbox-slideout', 'Inbox', import('./Notifications'));

  fireTag('link', {
    event_name: 'my account overlay',
    link_name: 'notifications',
  });

  sessionStorage.setItem(
    'which-notifications-read',
    JSON.stringify({
      ...notifications.value,
    }),
  );
  sessionStorage.setItem('notifications-read', userId);
  showIndicator(false);
};

onMounted(async () => {
  const userId = Cookie.get(onlineUid);
  const pageType = localStorage.getItem('pageType');
  await notificationStore.fetchNotifications({
    userId,
    pageType,
    deviceType: envPropsStore.deviceType,
  });

  const updatedIndicator = updateIndicator();
  showIndicator(updatedIndicator);
});
</script>

<style lang="scss" scoped>
.notif-indicator {
  position: absolute;
  left: rem-calc(15);
  bottom: rem-calc(12);
  height: rem-calc(14);
  width: rem-calc(14);
  border-radius: 50%;
  font-size: rem-calc(10);
  padding: rem-calc(1);
  display: flex;
  justify-content: center;
  align-items: center;
}

.notif-icon {
  cursor: pointer;
  position: relative;
  border: none;
}

.notif-icon-wrapper {
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '@csgIcons/_email-filled.scss';
.notif-indicator {
  color: $background-color;
  background-color: $primary-color;
}
</style>
<style lang="scss" brand="bcom" scoped>
@import '~@core/csg/src/scss/encodedIcons/bcom/_email.scss';
.notif-indicator {
  color: $white;
  background-color: $highlight-color;
}
</style>
